import { axios } from 'api'
import { FieldArray, Form, Formik } from 'formik'
import { DeleteIcon } from 'images/latest'
import React, { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import Collapse from 'ui/bend/animations/Collapse'
import * as Yup from 'yup'
import {
  AddButton,
  DragButtonComponent,
  Main,
  RemoveButton,
  SectionHeader,
  SectionWrapper
} from './Components'
import SectionItem from './SectionItem'
import SubmitButtonComponent from './SubmitButton'
import SectionTitleRow from './SectionTitleRow'
import Draggable from 'react-draggable'
import { LeftChevron } from 'images'

const slug =
  window.location.pathname.split('/').slice(-2, -1).toString() || null

const validationSchema = Yup.object({
  sections: Yup.array().of(
    Yup.object({
      title: Yup.string().required('Title is required'),
      items: Yup.array()
        .of(
          Yup.object({
            type: Yup.string().required('Item type is required'),
            id: Yup.string().required('Item ID is required'),
            series: Yup.string().optional(),
            itemTag: Yup.string().optional(),
            description: Yup.string().optional()
          })
        )
        .min(1, 'At least one item is required')
    })
  )
})

const ContentStructure = ({
  bonusItems = [],
  classes = [],
  seriesItems = []
}) => {
  const [initialValues, setInitialValues] = useState(null)
  const [isSubmitting, setisSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [classMap, setClassMap] = useState({})

  const getClassListbyPlan = async (plan_id) => {
    if (classMap[`plan_${plan_id}`]) return
    try {
      const res = await axios.post(
        `plans/get_classes`,
        { plan_id },
        { baseURL: '/admin/api/' }
      )
      const data = res.data?.classes[plan_id] || []
      setClassMap((prevState) => ({
        ...prevState,
        [`plan_${plan_id}`]: data
      }))
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    const getStructureAndSeries = async () => {
      try {
        const [structureRes] = await Promise.all([
          axios.get(`plans/get_structure/${slug}`, { baseURL: '/admin/api/' })
        ])
        const structureData = structureRes.data?.structure || []
        setInitialValues(structureData)

        const seriesIds = structureData.flatMap(({ items }) =>
          items?.map((item) => item.series).filter(Boolean)
        )
        if (seriesIds.length > 0) {
          const classRes = await axios.post(
            `plans/get_classes`,
            { plan_id: seriesIds },
            { baseURL: '/admin/api/' }
          )
          const classData = classRes.data?.classes || {}
          const newClassMap = seriesIds.reduce(
            (acc, id) => ({
              ...acc,
              [`plan_${id}`]: classData[id] || []
            }),
            {}
          )
          setClassMap(newClassMap)
        }
      } catch (err) {
        console.error(err)
        setInitialValues([])
      }
    }
    getStructureAndSeries()
  }, [])

  const handleSubmit = (values) => {
    const sections = values.sections.map(({ items, ...section }) => ({
      ...section,
      items: items.map(
        ({ type = 'class', id, series, itemTag = '', description = '' }) => {
          const getId = () => {
            if (id) return String(id)
            if (type === 'class') return String(classes[0]?.id)
            if (type === 'bonus') return String(bonusItems[0]?.id)
            return ''
          }

          return {
            type: type === 'class_from_another_series' ? 'class' : type,
            series: String(series),
            id: getId(),
            item_tag: itemTag,
            description
          }
        }
      )
    }))
    setisSubmitting(true)
    setSuccess(false)
    axios
      .post(
        `plans/update_structure/${slug}`,
        { sections },
        { baseURL: '/admin/api/' }
      )
      .then((res) => {
        if (res.data?.status === 'ok') {
          setSuccess(true)
        }
        setisSubmitting(false)
      })
      .catch((err) => {
        console.error(err)
        setisSubmitting(false)
      })
  }

  const [openSections, setOpenSections] = useState({})

  const toggleSection = (index) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  if (!initialValues) return null

  return (
    <Main>
      <Formik
        initialValues={{ sections: initialValues }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <FieldArray name='sections'>
              {({ remove, push, move }) => (
                <>
                  {values.sections.map((section, sectionIndex) => (
                    <Draggable
                      key={sectionIndex}
                      axis='y'
                      handle='.section-handle'
                      position={{ x: 0, y: 0 }}
                      onStart={() => setOpenSections({})}
                      onStop={(_, data) => {
                        const newIndex = sectionIndex + Math.round(data.y / 120)
                        move(sectionIndex, newIndex)
                      }}
                    >
                      <SectionWrapper key={sectionIndex}>
                        <div className='flex-box'>
                          <div className='left'>
                            <DragButtonComponent />
                            <RemoveButton
                              onClick={(e) => {
                                e.preventDefault()
                                toggleSection(sectionIndex)
                              }}
                              className={`collapse-button ${
                                openSections[sectionIndex] ? 'opened' : 'closed'
                              }`}
                            >
                              <LeftChevron />
                            </RemoveButton>
                            <SectionHeader>
                              Section {sectionIndex + 1}
                              {!openSections[sectionIndex] && section.title
                                ? ` - ${section.title}`
                                : ''}
                            </SectionHeader>
                          </div>

                          <RemoveButton
                            className='section section-delete'
                            type='button'
                            onClick={() => remove(sectionIndex)}
                          >
                            <DeleteIcon />
                          </RemoveButton>
                        </div>

                        <Collapse
                          isOpened={openSections[sectionIndex] || false}
                        >
                          <SectionTitleRow
                            errors={errors}
                            sectionIndex={sectionIndex}
                            touched={touched}
                            key={sectionIndex}
                          />

                          <FieldArray name={`sections[${sectionIndex}].items`}>
                            {({ remove, push, move: moveSectionIndex }) => (
                              <div className='section'>
                                {section.items.map((item, itemIndex) => {
                                  const itemList = (() => {
                                    if (
                                      item.type ===
                                        'class_from_another_series' &&
                                      classMap[`plan_${item.series}`]
                                    ) {
                                      return classMap[`plan_${item.series}`]
                                    }
                                    switch (item.type) {
                                      case 'bonus':
                                        return bonusItems
                                      case 'series':
                                        return seriesItems
                                      default:
                                        return classes
                                    }
                                  })()
                                  return (
                                    <SectionItem
                                      key={itemIndex}
                                      bonusItems={bonusItems}
                                      errors={errors}
                                      getClassListbyPlan={getClassListbyPlan}
                                      item={item}
                                      itemIndex={itemIndex}
                                      sectionIndex={sectionIndex}
                                      itemList={itemList}
                                      remove={remove}
                                      seriesItems={seriesItems}
                                      setFieldValue={setFieldValue}
                                      moveSectionIndex={moveSectionIndex}
                                      length={section.items?.length}
                                    />
                                  )
                                })}
                                <Row>
                                  <Col>
                                    <AddButton
                                      type='button'
                                      className='btn btn-primary'
                                      onClick={() =>
                                        push({
                                          type: 'class',
                                          id: classes[0]?.id,
                                          series: '',
                                          itemTag: '',
                                          description: ''
                                        })
                                      }
                                    >
                                      Add Item
                                    </AddButton>
                                  </Col>
                                </Row>
                              </div>
                            )}
                          </FieldArray>
                        </Collapse>
                      </SectionWrapper>
                    </Draggable>
                  ))}
                  <Row>
                    <Col>
                      <AddButton
                        type='button'
                        className='btn btn-primary'
                        onClick={() => {
                          push({
                            title: '',
                            description: '',
                            items: [
                              {
                                type: 'class',
                                id: classes[0]?.id,
                                series: '',
                                itemTag: '',
                                description: ''
                              }
                            ]
                          })
                          setOpenSections((state) => ({
                            ...state,
                            [values.sections?.length]: true
                          }))
                        }}
                      >
                        Add Section
                      </AddButton>
                    </Col>
                  </Row>
                </>
              )}
            </FieldArray>
            <SubmitButtonComponent
              isSubmitting={isSubmitting}
              success={success}
              errors={errors}
              touched={touched}
            />
          </Form>
        )}
      </Formik>
    </Main>
  )
}

export default ContentStructure
